import React, { useCallback, useEffect, useState } from 'react';
import {
  Divider,
  Tag,
  Space,
  Typography,
  Button,
  Table,
  Tooltip,
  Flex,
  Modal,
  Input,
  Form,
  Popconfirm,
} from 'antd';
import {
  PlusCircleOutlined,
  RedoOutlined,
  EditOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  WhatsAppOutlined,
  SendOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import NumberCreateEdit from './CreateEdit';
import { ContainerTemplate } from '../Templates/styles';
import { PagePanel } from '../../../components';
import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';

const { Text, Paragraph } = Typography;

interface IWhatsappConfig {
  id: string;
  phone_number_id: string;
  waba_id: string;
  verify_token: string;
  status?: string;
  name?: string;
}

const WhatsappConnections: React.FC = () => {
  const { addToast } = useToast();
  const [configs, setConfigs] = useState<IWhatsappConfig[]>([]);
  const [currentConfig, setCurrentConfig] = useState<IWhatsappConfig | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  // --- Estados para a Modal de Teste ---
  const [testModalOpen, setTestModalOpen] = useState<boolean>(false);
  const [testingPhone, setTestingPhone] = useState<string>('');
  const [isSendingTest, setIsSendingTest] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const list = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/whatsapp/config');
      const data = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];
      setConfigs(data as IWhatsappConfig[]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(
    async (record: IWhatsappConfig) => {
      setDeletingId(record.id);
      try {
        await api.delete(`/whatsapp/config/${record.id}`);
        addToast({
          type: 'success',
          title: 'Excluído',
          description: `Configuração ${record.phone_number_id} foi excluída com sucesso.`,
        });
        await list();
      } catch (err: any) {
        addToast({
          type: 'error',
          title: 'Erro ao excluir',
          description:
            err?.response?.data?.message ||
            err?.response?.data ||
            err?.message ||
            'Não foi possível excluir a configuração.',
        });
      } finally {
        setDeletingId(null);
      }
    },
    [addToast, list],
  );

  useEffect(() => {
    list();
  }, [list]);

  // --- Função que dispara o Teste para a API Oficial ---
  const handleSendTest = async () => {
    if (!testingPhone || testingPhone.length < 10) {
      return addToast({
        type: 'warning',
        title: 'Atenção',
        description: 'Digite um número válido com DDI e DDD.',
      });
    }

    setIsSendingTest(true);
    try {
      // Remove espaços, parênteses e traços
      const cleanPhone = testingPhone.replace(/\D/g, '');

      // Dispara para o endpoint /whatsapp/send-template do seu backend
      await api.post('/whatsapp/send-template', {
        to: cleanPhone,
        template_name: 'hello_world', // Template padrão que a Meta aprova automaticamente
        language_code: 'en_US', // Idioma padrão do hello_world na Meta
        components: [],
      });

      addToast({
        type: 'success',
        title: 'Mensagem Enviada!',
        description:
          'Verifique o seu WhatsApp. O template "hello_world" foi disparado.',
      });
      setTestModalOpen(false);
      setTestingPhone('');
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro no Envio',
        description:
          err.response?.data?.message ||
          'Verifique se o número está autorizado na Meta.',
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  return (
    <ContainerTemplate>
      <PagePanel>
        <Flex justify="space-between" align="center" gap={16}>
          <Text strong style={{ fontSize: 20 }}>
            Números de WhatsApp
          </Text>
          <Flex gap={8}>
            <Button icon={<RedoOutlined />} onClick={list}>
              Atualizar
            </Button>
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => {
                setCurrentConfig(null);
                setModalOpen(true);
              }}
            >
              Novo Número
            </Button>
          </Flex>
        </Flex>
        <Divider />
        <Table
          loading={loading}
          dataSource={configs}
          rowKey="id"
          columns={[
            {
              title: 'Descrição',
              dataIndex: 'name',
              render: (text) => (
                <Text strong>{text || 'Configuração Principal'}</Text>
              ),
            },
            {
              title: 'Phone Number ID',
              dataIndex: 'phone_number_id',
              render: (val: string) => <Text copyable>{val}</Text>,
            },
            {
              title: 'WABA ID',
              dataIndex: 'waba_id',
              render: (val: string) => <Text type="secondary">{val}</Text>,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              render: () => (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  Conectado
                </Tag>
              ),
            },
            {
              title: 'Ações',
              width: 140,
              render: (record: IWhatsappConfig) => (
                <Space>
                  <Tooltip title="Editar">
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setCurrentConfig(record);
                        setModalOpen(true);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Testar Ligação">
                    <Button
                      type="text"
                      icon={<SyncOutlined style={{ color: '#1890ff' }} />}
                      onClick={() => setTestModalOpen(true)}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="Excluir esta conexão?"
                    description={
                      <span>
                        Esta ação não pode ser revertida. O número
                        <b> {record.phone_number_id}</b> será removido do
                        tenant permanentemente.
                      </span>
                    }
                    okText="Sim, excluir"
                    cancelText="Cancelar"
                    okButtonProps={{
                      danger: true,
                      loading: deletingId === record.id,
                      disabled: deletingId !== null && deletingId !== record.id,
                    }}
                    cancelButtonProps={{
                      disabled: deletingId !== null,
                    }}
                    onConfirm={() => handleDelete(record)}
                  >
                    <Tooltip title="Excluir">
                      <Button
                        type="text"
                        danger
                        loading={deletingId === record.id}
                        disabled={deletingId !== null && deletingId !== record.id}
                        icon={<DeleteOutlined />}
                      />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </PagePanel>
      {modalOpen && (
        <NumberCreateEdit
          open={modalOpen}
          close={() => setModalOpen(false)}
          config={currentConfig}
          onSuccess={list}
        />
      )}

      {/* Modal de Disparo de Teste */}
      <Modal
        title={
          <span>
            <SendOutlined /> Testar Conexão WhatsApp
          </span>
        }
        open={testModalOpen}
        onCancel={() => setTestModalOpen(false)}
        onOk={handleSendTest}
        okText="Enviar Hello World"
        cancelText="Cancelar"
        confirmLoading={isSendingTest}
        destroyOnClose
      >
        <Paragraph>
          Insira o seu número de telemóvel para receber o template de
          boas-vindas da Meta e validar a integração.
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: '12px' }}>
          <b>Nota:</b> Como o seu App está em modo Desenvolvimento na Meta, este
          número DEVE estar registado na lista de números de teste do painel da
          Cloud API.
        </Paragraph>

        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="Número de Destino (DDI + DDD + Número)" required>
            <Input
              placeholder="Ex: 5511999999999"
              value={testingPhone}
              onChange={(e) => setTestingPhone(e.target.value)}
              size="large"
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </ContainerTemplate>
  );
};

export default WhatsappConnections;
